import AppButton from '@nxsys/components/AppButton';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconDelete, IconEdit } from '@nxsys/components/AppIconSvg';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useCreatePrivateIrelandPension,
  useDeletePrivateIrelandPension,
  useGetPrivateIrelandPension,
  useToggleDefaultPrivateIrelandPension,
  useUpdatePrivateIrelandPension,
} from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { IPrivateIrelandPension } from '@nxsys/types/models/pension';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrivatePensionFormValues } from './PrivatePensionForm';
import CreatePrivatePensionModal from './CreatePrivatePensionModal';
import {
  CreatePrivateIrelandPensionParams,
  UpdatePrivateIrelandPensionParams,
} from '@nxsys/types/api/company';
import { showMessage } from '@nxsys/utils/message';
import EditPrivatePensionModal from './EditPrivatePensionModal';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';

type Props = {
  data?: ICompany;
};

const PrivatePensionSchemes: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const {
    value: privateIrelandPension,
    loading: privateIrelandPensionLoading,
    run: getPrivateIrelandPension,
  } = useGetPrivateIrelandPension();

  const { run: toggleDefaultPension } =
    useToggleDefaultPrivateIrelandPension();

  useEffect(() => {
    if (data?.id) {
      getPrivateIrelandPension(data.id);
    }
  }, [data]);

  const handleToggleDefault = async (record: IPrivateIrelandPension) => {
    if (!data?.id) return;
    const res = await toggleDefaultPension(data.id, record.id);
    if (res) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      getPrivateIrelandPension(data.id);
    }
  };

  const formatContribution = (
    value: number | string,
    type?: EeErPensionMethodEnum,
  ) => {
    if (!value) return '-';
    if (type === EeErPensionMethodEnum.PERCENTAGE) {
      return `${value}%`;
    }
    return formatMoney(
      typeof value === 'string' ? parseFloat(value) : value,
      showCurrencyByCountry(payrollCountry),
    );
  };

  const columns: ColumnsType<IPrivateIrelandPension> = [
    {
      key: 'actions',
      title: messages['table.actions'],
      fixed: 'left',
      align: 'left',
      width: 120,
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li>
            <AppIconButton
              title={messages['common.edit']}
              icon={<IconEdit />}
              onClick={() => handleOpenEditPrivatePensionModal(record)}
            />
          </li>
          <li>
            <AppIconButton
              title={messages['common.delete']}
              icon={<IconDelete />}
              onClick={() => handleDeletePrivatePension(record.id)}
            />
          </li>
        </ul>
      ),
    },
    {
      key: 'isDefault',
      title: messages['common.enabled'],
      width: 100,
      render: (record) => (
        <AppSwitch
          checked={record.isDefault}
          onChange={() => handleToggleDefault(record)}
        />
      ),
    },
    {
      key: 'name',
      title: messages['common.name'],
      dataIndex: 'name',
      width: 150,
    },
    {
      key: 'type',
      title: messages['common.type'],
      dataIndex: 'type',
      width: 120,
      render: (type: string) => <Tag color="cyan">{type}</Tag>,
    },
    {
      key: 'reference',
      title: messages['common.reference'],
      dataIndex: 'reference',
      width: 150,
    },
    {
      key: 'employee',
      title: messages['common.employee'],
      width: 120,
      render: (record) =>
        formatContribution(record.eePensionValue, record.eePensionType),
    },
    {
      key: 'employer',
      title: messages['common.employer'],
      width: 120,
      render: (record) =>
        formatContribution(record.erPensionValue, record.erPensionType),
    },
    {
      key: 'avc',
      title: messages['common.avc'],
      width: 120,
      render: (record) =>
        formatContribution(record.avcPensionValue, record.avcPensionType),
    },
  ];

  const [openCreatePrivatePensionModal, setOpenCreatePrivatePensionModal] =
    useState(false);
  const { run: createPrivateIrelandPension } = useCreatePrivateIrelandPension();
  const handleCreatePrivatePension = async (
    values: PrivatePensionFormValues,
  ) => {
    if (!data?.id) return;
    const params: CreatePrivateIrelandPensionParams = {
      name: values.name,
      type: values.type?.value || '',
      reference: values.reference,
      eePensionValue: values.eePensionValue,
      eePensionType: values.eePensionType?.value || '',
      erPensionValue: values.erPensionValue,
      erPensionType: values.erPensionType?.value || '',
      avcPensionValue: values.avcPensionValue,
      avcPensionType: values.avcPensionType?.value || '',
    };
    const res = await createPrivateIrelandPension(data?.id, params);
    if (!!res) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      setOpenCreatePrivatePensionModal(false);
      getPrivateIrelandPension(data.id);
    }
  };

  const [openEditPrivatePensionModal, setOpenEditPrivatePensionModal] =
    useState<boolean>(false);
  const [selectedData, setSelectedData] =
    useState<IPrivateIrelandPension | null>(null);

  const handleOpenEditPrivatePensionModal = (data: IPrivateIrelandPension) => {
    setSelectedData(data);
    setOpenEditPrivatePensionModal(true);
  };
  const { run: updatePrivateIrelandPension } = useUpdatePrivateIrelandPension();
  const handleEditPrivatePension = async (values: PrivatePensionFormValues) => {
    if (!data?.id || !selectedData?.id) return;
    const params: UpdatePrivateIrelandPensionParams = {
      name: values.name,
      type: values.type?.value || '',
      reference: values.reference,
      eePensionValue: values.eePensionValue,
      eePensionType: values.eePensionType?.value || '',
      erPensionValue: values.erPensionValue,
      erPensionType: values.erPensionType?.value || '',
      avcPensionValue: values.avcPensionValue,
      avcPensionType: values.avcPensionType?.value || '',
    };
    const res = await updatePrivateIrelandPension(
      data.id,
      selectedData.id,
      params,
    );
    if (!!res) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      setOpenEditPrivatePensionModal(false);
      getPrivateIrelandPension(data.id);
    }
  };

  const { run: deletePrivateIrelandPension } = useDeletePrivateIrelandPension();
  const handleDeletePrivatePension = async (pensionId: number) => {
    if (!data?.id) return;
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        await deletePrivateIrelandPension(data.id, pensionId);
        showMessage({
          type: 'success',
          content: messages['common.deleteSuccess'],
        });
        getPrivateIrelandPension(data.id);
      },
    });
  };

  return (
    <div className="PrivatePensionSchemes">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[14px] font-semibold">
          {messages['common.privatePensionSchemes']}
        </div>
        <AppButton
          name={messages['common.addPensionScheme']}
          leftIcon={<Icons.IconPlus color={defaultTheme.theme.palette.white} />}
          onClick={() => setOpenCreatePrivatePensionModal(true)}
        />
      </div>
      <AppTableContainer
        rowKey="id"
        columns={columns}
        data={privateIrelandPension ?? []}
        loading={privateIrelandPensionLoading}
        pagination={false}
        page={1}
        total={privateIrelandPension?.length ?? 0}
        pageSize={privateIrelandPension?.length ?? 0}
        scroll={{ x: 1000 }}
      />
      <CreatePrivatePensionModal
        isOpen={openCreatePrivatePensionModal}
        toggle={() => setOpenCreatePrivatePensionModal(false)}
        onSubmit={handleCreatePrivatePension}
      />
      {selectedData && openEditPrivatePensionModal && (
        <EditPrivatePensionModal
          isOpen={openEditPrivatePensionModal}
          toggle={() => setOpenEditPrivatePensionModal(false)}
          onSubmit={handleEditPrivatePension}
          data={selectedData}
        />
      )}
    </div>
  );
};

export default PrivatePensionSchemes;
